// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useState } from 'react';

import type { RoomInfo } from '../../bygning/rooms';
import type { Selection } from '../../bygning/selection';
import type { Storey } from '../../bygning/storeys';
import type { EnvFilter } from '../../kortlaegning/model';
import { Select } from '../Select';
import { SearchInput } from '../TextInput';
import { ToggleChip } from '../ToggleChip';
import { RoomScope } from './RoomScope';
import toolbar from './toolbar.module.css';

export interface ResourceFiltersProps {
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  /** The building's rooms (`roomsOf`), for the room picker. */
  rooms: readonly RoomInfo[];
  /** The storeys to group them by (`useStoreys`). */
  storeys: readonly Storey[];
  /**
   * Stacked in a column (the Model's left tile): the controls take the
   * column's width (it stretches them), the chips share a row.
   */
  block?: boolean;
}

/**
 * Ressourcer's filters, one set for Model, Tabel and Galleri (all in the
 * URL, `bygning/resourceFilter`): the room focus, the search (type, EAK,
 * part code, room), Miljøstatus, Vigtige ★ and Fejldetektion. A fragment,
 * so it lays out in whichever bar or column holds it.
 */
export function ResourceFilters({ sel, setSel, rooms, storeys, block }: ResourceFiltersProps) {
  // The search types into a local draft: the router navigates in a transition, so an input
  // driven by `?q=` alone drops keystrokes and moves the caret (æøå, IME). The draft follows
  // the URL only while the field is not focused (Nulstil filtre, a mode switch, Back).
  const [draft, setDraft] = useState(sel.search);
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    if (!typing) setDraft(sel.search);
  }, [sel.search, typing]);
  const chips = (
    <>
      <ToggleChip
        className={toolbar.chip}
        pressed={sel.starred}
        onChange={(starred) => setSel({ starred })}
        icon={<span className={toolbar.star}>★</span>}
        title="Vis kun vigtige"
      >
        Vigtige
      </ToggleChip>
      <ToggleChip
        className={toolbar.chip}
        pressed={sel.errors}
        onChange={(errors) => setSel({ errors })}
        title="Vis typer og dele markeret som fejldetektion"
      >
        Fejldetektion
      </ToggleChip>
    </>
  );
  return (
    <>
      <RoomScope rooms={rooms} storeys={storeys} value={sel.room} onChange={(room) => setSel({ room })} />
      <SearchInput
        variant="filled"
        size="md"
        className={block ? undefined : toolbar.search}
        placeholder="Søg type, del eller rum…"
        aria-label="Søg i ressourcer"
        value={draft}
        onFocus={() => setTyping(true)}
        onBlur={() => setTyping(false)}
        onChange={(e) => {
          setDraft(e.target.value);
          setSel({ search: e.target.value });
        }}
      />
      <Select
        variant="filled"
        size="md"
        aria-label="Miljøstatus"
        value={sel.env ?? ''}
        onChange={(e) => setSel({ env: e.target.value === '' ? null : (e.target.value as EnvFilter) })}
      >
        <option value="">Alle miljøstatus</option>
        <option value="ren">Ren</option>
        <option value="afventer">Afventer prøve</option>
        <option value="forurenet">Forurenet</option>
      </Select>
      {block ? <div className={toolbar.chipRow}>{chips}</div> : chips}
    </>
  );
}
